'use client';

import {
  CallToActionProps,
  Container,
  SimpleProfileCardsGridSection,
  TagFilter
} from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import { useEffect, useState } from 'react';
import flush from 'just-flush';
import omit from 'just-omit';
import { researchPlatformsQuery } from './researchPlatformsQuery';
import { filterOptionsFromDocuments, findInKeyArray } from '@/utils';
import entryAsCardProps from './entryAsCardProps';
import { useClientUrl } from '@/hooks';

type Props = {
  title?: string;
  subtitle?: string;
  action?: CallToActionProps;
  enableTaxonomyFilter?: boolean;
  hideCardImages?: boolean;
  platforms: (typeof researchPlatformsQuery)[1];
  defaultFilters?: string[];
};

export default function ResearchPlatformListingSection({
  title,
  subtitle,
  action,
  enableTaxonomyFilter,
  hideCardImages,
  platforms,
  defaultFilters
}: Props) {
  const url = useClientUrl();
  const { t, lang } = useTranslation();
  const [selected, setSelected] = useState<string[]>(
    defaultFilters?.length ? defaultFilters : ['all']
  );

  const sortedGroups = platforms.sort((a, b) => {
    const aSortKey = findInKeyArray(a.title ?? [], [
      lang,
      defaultLanguage.id
    ])?.value;
    const bSortKey = findInKeyArray(b.title ?? [], [
      lang,
      defaultLanguage.id
    ])?.value;
    if (aSortKey && bSortKey) {
      const comparison = aSortKey.localeCompare(bSortKey);
      return comparison;
    }
    return 0;
  });

  const filteredGroups = sortedGroups.filter((group) => {
    if (selected.length === 1 && selected[0] === 'all') return true;
    if (!group.taxonomies?.length) return false;
    return group.taxonomies.some(
      (tax) => tax._id && selected.includes(tax._id)
    );
  });

  const filters = filterOptionsFromDocuments(sortedGroups, (doc) =>
    flush(
      doc.taxonomies?.map((tax) => {
        if (tax.type !== TaxonomyType.DISEASE_AREA) return null;
        const name = tax.name
          ? findInKeyArray(tax.name, [lang, defaultLanguage.id])
          : null;
        if (!tax._id || !tax.slug?.current || !name?.value) return null;
        return { label: name.value, value: tax._id, slug: tax.slug.current };
      }) ?? []
    )
  ).sort((a, b) => a.label.localeCompare(b.label));

  const filtersParam = url?.searchParams.get('filters');
  useEffect(() => {
    const filtersParamOptions = flush(
      (filtersParam?.split(',') ?? []).map((value) =>
        filters.find((entry) => entry.value === value || entry.slug === value)
      )
    );
    if (filtersParamOptions.length) {
      setSelected(filtersParamOptions.map((opt) => opt.value));
    }
  }, [filtersParam]);

  return (
    <>
      {enableTaxonomyFilter && (
        <Container name={ResearchPlatformListingSection.name}>
          <TagFilter
            value={selected}
            options={[{ label: t('global:all'), value: 'all' }, ...filters]}
            onChange={setSelected}
          />
        </Container>
      )}
      <Container name={ResearchPlatformListingSection.name}>
        <SimpleProfileCardsGridSection
          title={title}
          subtitle={subtitle}
          action={action}
          cards={flush(
            filteredGroups
              .map((group) => entryAsCardProps(group, lang))
              .map((cardProps) => {
                if (cardProps && hideCardImages) {
                  return omit(cardProps, ['image']);
                }
                return cardProps;
              })
          )}
        />
      </Container>
    </>
  );
}
